import { styled } from "goober";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  CursorTooltip,
  TooltipLabel,
  TooltipRow,
  TooltipTitle,
  TooltipValue,
} from "./CursorTooltip";
import { Link } from "./Link";
import type { StatusSegment } from "./StatusBreakdownChart";
import { Text } from "./Text";
import { isInAppNavigation } from "./Link";

const MIN_HEIGHT = 130;
const GAP = 4;
const TOOLTIP_MAX_ROWS = 8;

// Ghost tiles are gray-400 rather than an alpha token: the 28% color-mix tint
// below would collapse an 8%-alpha color to near-invisible.
const GHOST_SEGMENTS: StatusSegment[] = [
  { key: "ghost-1", count: 5, color: "var(--gray-400)", label: "" },
  { key: "ghost-2", count: 3, color: "var(--gray-400)", label: "" },
  { key: "ghost-3", count: 2, color: "var(--gray-400)", label: "" },
  { key: "ghost-4", count: 1, color: "var(--gray-400)", label: "" },
  { key: "ghost-5", count: 1, color: "var(--gray-400)", label: "" },
];

const Container = styled("div")`
  position: relative;
  flex: 1;
  width: 100%;
  min-height: ${MIN_HEIGHT}px;
  overflow: hidden;
`;

const tileStyles = `
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  border-radius: 8px;
  overflow: hidden;
  text-align: left;
`;

const TileLink = styled(Link)`
  ${tileStyles}
  text-decoration: none;
  transition: filter 120ms ease;

  &:hover {
    filter: brightness(0.96);
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const TileBox = styled("div")`
  ${tileStyles}
`;

const TileLabel = styled("div")`
  display: flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  min-width: 0;
`;

const Dot = styled("span")`
  width: 7px;
  height: 7px;
  border-radius: 2px;
  flex-shrink: 0;
`;

type Item = { seg: StatusSegment; area: number };
type Rect = { seg: StatusSegment; x: number; y: number; w: number; h: number };

function worstRatio(areas: number[], side: number): number {
  const sum = areas.reduce((a, b) => a + b, 0);
  const max = Math.max(...areas);
  const min = Math.min(...areas);
  const s2 = sum * sum;
  const side2 = side * side;
  return Math.max((side2 * max) / s2, s2 / (side2 * min));
}

// Squarified treemap (Bruls, Huizing & van Wijk): greedily packs rows that keep
// tiles close to square, recursing into the leftover rectangle.
function squarify(items: Item[], width: number, height: number): Rect[] {
  const result: Rect[] = [];
  let rest = [...items];
  let x = 0;
  let y = 0;
  let w = width;
  let h = height;

  while (rest.length > 0) {
    const side = Math.min(w, h);
    const row: Item[] = [];
    const rowAreas: number[] = [];
    while (rest.length > 0) {
      const next = rest[0]!;
      const candidate = [...rowAreas, next.area];
      if (
        row.length === 0 ||
        worstRatio(rowAreas, side) >= worstRatio(candidate, side)
      ) {
        row.push(next);
        rowAreas.push(next.area);
        rest = rest.slice(1);
      } else {
        break;
      }
    }

    const rowArea = rowAreas.reduce((a, b) => a + b, 0);
    if (w >= h) {
      const stripW = rowArea / h;
      let cy = y;
      for (const cell of row) {
        const cellH = cell.area / stripW;
        result.push({ seg: cell.seg, x, y: cy, w: stripW, h: cellH });
        cy += cellH;
      }
      x += stripW;
      w -= stripW;
    } else {
      const stripH = rowArea / w;
      let cx = x;
      for (const cell of row) {
        const cellW = cell.area / stripH;
        result.push({ seg: cell.seg, x: cx, y, w: cellW, h: stripH });
        cx += cellW;
      }
      y += stripH;
      h -= stripH;
    }
  }
  return result;
}

type Props = {
  segments: ReadonlyArray<StatusSegment>;
  formatValue?: (value: number) => string;
  segmentUrl?: (segment: StatusSegment) => string | undefined;
  onSegmentNavigate?: (segment: StatusSegment) => void;
  placeholder?: boolean;
};

export function Treemap({
  segments,
  formatValue = (n) => String(n),
  segmentUrl,
  onSegmentNavigate,
  placeholder,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () =>
      setSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { width, height } = size;
  const shown = placeholder
    ? GHOST_SEGMENTS
    : segments.filter((s) => s.count > 0);
  const total = shown.reduce((sum, seg) => sum + seg.count, 0);

  let rects: Rect[] = [];
  if (width > 0 && height > 0 && total > 0 && shown.length > 0) {
    const sorted = [...shown].sort((a, b) => b.count - a.count);
    const scale = (width * height) / total;
    const items = sorted.map((seg) => ({ seg, area: seg.count * scale }));
    rects = squarify(items, width, height);
  }

  const hoveredSeg = hoveredKey
    ? (rects.find((r) => r.seg.key === hoveredKey)?.seg ?? null)
    : null;

  return (
    <Container ref={ref} aria-hidden={placeholder || undefined}>
      {rects.map((rect) => {
        const left = rect.x + GAP / 2;
        const top = rect.y + GAP / 2;
        const tileW = Math.max(
          Math.min(rect.x + rect.w, width) - GAP / 2 - left,
          1
        );
        const tileH = Math.max(
          Math.min(rect.y + rect.h, height) - GAP / 2 - top,
          1
        );
        const showText = !placeholder && tileW >= 64 && tileH >= 40;
        const hasDetail =
          (rect.seg.details?.length ?? 0) > 0 ||
          (rect.seg.items?.length ?? 0) > 0;
        const interactive = !placeholder && (!showText || hasDetail);
        const url = placeholder ? undefined : segmentUrl?.(rect.seg);
        const tileProps = {
          key: rect.seg.key,
          "aria-label": placeholder
            ? undefined
            : `${rect.seg.label}: ${formatValue(rect.seg.count)}`,
          style: {
            left,
            top,
            width: tileW,
            height: tileH,
            // contentless tiles get no padding: a tiny tile can be narrower than it, which border-box can't shrink below, so it would overflow its cell
            padding: showText ? "9px 10px" : 0,
            background: `color-mix(in srgb, ${rect.seg.color} 28%, var(--surface-primary))`,
          },
          onMouseEnter: interactive
            ? (e: React.MouseEvent) => {
                setHoveredKey(rect.seg.key);
                setPointer({ x: e.clientX, y: e.clientY });
              }
            : undefined,
          onMouseMove: interactive
            ? (e: React.MouseEvent) =>
                setPointer({ x: e.clientX, y: e.clientY })
            : undefined,
          onMouseLeave: interactive
            ? () => {
                setHoveredKey(null);
                setPointer(null);
              }
            : undefined,
        };
        const content: ReactNode = showText && (
          <>
            <TileLabel>
              <Dot style={{ background: rect.seg.color }} />
              <Text
                variant="text-small-md"
                color="var(--text-primary)"
                ellipsis
              >
                {rect.seg.label}
              </Text>
            </TileLabel>
            <Text variant="text-small" color="var(--text-secondary)">
              {formatValue(rect.seg.count)}
            </Text>
          </>
        );
        return url ? (
          <TileLink
            {...tileProps}
            to={url}
            onPointerDown={(e) => {
              if (isInAppNavigation(e)) onSegmentNavigate?.(rect.seg);
            }}
            data-no-pill
          >
            {content}
          </TileLink>
        ) : (
          <TileBox {...tileProps}>{content}</TileBox>
        );
      })}
      {hoveredSeg && pointer ? (
        <CursorTooltip x={pointer.x} y={pointer.y} contentKey={hoveredSeg.key}>
          <TooltipTitle>{hoveredSeg.label}</TooltipTitle>
          <TooltipLabel>{formatValue(hoveredSeg.count)}</TooltipLabel>
          {hoveredSeg.details && (
            <>
              {hoveredSeg.details.slice(0, TOOLTIP_MAX_ROWS).map((d) => (
                <TooltipRow key={d.label}>
                  <TooltipLabel>{d.label}</TooltipLabel>
                  <TooltipValue>{d.value}</TooltipValue>
                </TooltipRow>
              ))}
              {hoveredSeg.details.length > TOOLTIP_MAX_ROWS && (
                <TooltipLabel>
                  +{hoveredSeg.details.length - TOOLTIP_MAX_ROWS} more
                </TooltipLabel>
              )}
            </>
          )}
          {hoveredSeg.items && (
            <>
              {hoveredSeg.items.slice(0, TOOLTIP_MAX_ROWS).map((item) => (
                <TooltipLabel key={item}>{item}</TooltipLabel>
              ))}
              {hoveredSeg.items.length > TOOLTIP_MAX_ROWS && (
                <TooltipLabel>
                  +{hoveredSeg.items.length - TOOLTIP_MAX_ROWS} more
                </TooltipLabel>
              )}
            </>
          )}
        </CursorTooltip>
      ) : null}
    </Container>
  );
}
